// @flow

import type {Reducer} from 'redux';
import type {ActionCreator, Dispatch, GetState, RootState} from '../reducers/types';

type CallbackParams = {|state: RootState|};
type Callback<T> = (CallbackParams, ...actionCreatorArgs: Array<mixed>) => T;

const defaultAcReturnValue = () => undefined;

// Prevent dispatching an action based on condition from predicate.
// If predicate returns true, the passed action creator is not called,
// and acReturnValue is called instead and its result acts as action
// creator return value
export const preventActionWhen = (
  predicate: Callback<boolean>,
  acReturnValue: Callback<mixed> = defaultAcReturnValue
) => (actionCreator: (args: mixed) => ActionCreator) => {
  const enhancedActionCreator = (...actionCreatorArgs: Array<mixed>) => (
    dispatch: Dispatch,
    getState: GetState
  ) => {
    const shouldPreventActionDispatch = predicate({state: getState()}, ...actionCreatorArgs);

    return shouldPreventActionDispatch
      ? acReturnValue({state: getState()}, ...actionCreatorArgs)
      : dispatch(actionCreator(...actionCreatorArgs));
  };
  return enhancedActionCreator;
};

export function composeReducers<S, _Action>(reducers: Array<Reducer<S, _Action>>) {
  return <A: _Action>(state: S, action: A) =>
    reducers.reduce((_state: S, reducer: Reducer<S, _Action>) => reducer(_state, action), state);
}
